import {GridDataTable, GridDataTableColumn, GridDataTableColumnGroup, GridDataTableColumnType, GridDataTableItem, GridDataTableRow} from '@/component/grid-data-table/grid-data-table-model';
import {Filter, FilterTypes} from '@/component/filter-sidebar/model'
import {formatMoney} from '@/utils/currency'
import {roundToThree} from '@/utils/helper'
import moment from 'moment'


export const ReportDateGroupBy = {
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  QUARTER: "quarter",
  YEAR: "year"
}

export const ReportIncomeGridDataTableItemType = {
  TEXT: 1,
  CURRENCY: 2,
  PERCENT: 3
}

export class ReportTypeIncome {
  constructor(data = {}) {
    this.name = data.name || null
    this.value = data.value || null
  }

  static get ENUM() {
    return {
      VENDOR: 1,
      PAYMENT_TYPE: 2,
      DOCUMENT_TYPE: 3,
      CATEGORY: 4
    }
  }
}

export class ReportMetricIncome {
  constructor(data = {}) {
    this.name = data.name || null
    this.value = data.value || null
  }

  static get ENUM() {
    return {
      PRICES: 1,
      PRICES_WITH_TAX: 2,
      BUY_PRICE: 3,
      GROSS_PROFIT: 4
    }
  }
}

export class ReportIncomeRequest {
  // Data is instance of ReportIncome
  constructor(data = {}) {
    this.income_type = data.income_type || null
    this.report_type = data.report_type || null
    this.group_by = data.group_by || null
    this.main_metric = data.main_metric || null
    this.date_start = data.date_start || null
    this.date_end = data.date_end || null
    this.vendor = data.vendor || null
    this.tag = data.tag || null
    this.paymentType = data.paymentType || null
    this.documentType = data.documentType || null
    this.category = data.category || null
  }
}

export class ReportIncomeExportRequest extends ReportIncomeRequest {
  constructor(data = {}) {
    super(data)
    this.export = data.export || "xls"
  }
}

export class ReportIncomeGridDataTableItem extends GridDataTableItem {
  constructor(data = {}) {
    super(data)
    this.type = data.type || ReportIncomeGridDataTableItemType.TEXT
    this.variants = data.variants || []
  }

  get isTextItem() {
    return this.type === ReportIncomeGridDataTableItemType.TEXT
  }

  get isCurrencyItem() {
    return this.type === ReportIncomeGridDataTableItemType.CURRENCY
  }

  get isPercentItem() {
    return this.type === ReportIncomeGridDataTableItemType.PERCENT
  }

  get isTextAndVariantsItem() {
    return this.type === ReportIncomeGridDataTableItemType.TEXT_AND_VARIANTS
  }
}

export class ReportIncomeGridDataTableFactory {
  constructor(reportIncome, response) {
    this.$t = reportIncome.$t
    this.reportIncome = reportIncome
    this.response = response
  }

  create = () => {
    let columns = this._createColumns()
    let rows = this._createRows()
    let columnGroups = this._createColumnGroups(columns)
    let self = this

    return new GridDataTable({
      columnGroups: columnGroups,
      rows: rows,
      searchColumnKeys: ["target"],
      footerRow: self._createFooterRow(self.$t),
      isLocal: true,
      arrangeTableWide: true,
      emptyResultText: self.$t('generic.msg.no_data')
    })
  }

  _createColumnGroups = (columns) => {
    let columnGroups = [
      new GridDataTableColumnGroup({
        columns: [columns.firstColumn],
        isFixed: true
      })
    ]

    if (columns.dateRangeColumns && columns.dateRangeColumns.length) {
      columnGroups.push(new GridDataTableColumnGroup({
        id: "incomes",
        columns: columns.dateRangeColumns
      }));
    }

    columnGroups.push(new GridDataTableColumnGroup({
      columns: columns.summaryColumns,
      isFixed: false,
      isArrangable: true,
      disableStateSave: true,
      id: "income_report"
    }))

    return columnGroups
  }

  _createColumns = () => {
    let columns = {}
    columns.firstColumn = this._createFirstColumn()
    columns.dateRangeColumns = this._createDateRangeColumns()
    columns.summaryColumns = this._createSummaryColumns()
    return columns
  }

  _createFirstColumn = () => {
    let self = this
    return new GridDataTableColumn({
      key: "target",
      type: GridDataTableColumnType.SINGLE_LINE_TEXT,
      text: self.reportIncome.reportType.name,
      textAlign: 'left',
      isSortable: true,
      isActiveSortColumn: true
    });
  }

  _createDateRangeColumns = () => {
    const dateList = this.response.date_list
    const targetIdList = this.response.target_id_list

    let dateRangeColumns = []
    if (dateList && dateList.length) {
      let previousColumnYear = parseInt(dateList[0].substring(0, 4))
      for (let i = 0; i < dateList.length; i++) {
        const dateStr = dateList[i]
        const year = parseInt(dateStr.substring(0, 4))
        const dateTexts = this._getDateColumnTexts(dateStr)
        const type = Object.keys(dateTexts).length === 2 ? GridDataTableColumnType.DOUBLE_LINE_TEXT : GridDataTableColumnType.SINGLE_LINE_TEXT
        dateRangeColumns.push(new GridDataTableColumn({
          key: dateStr,
          type: type,
          text: dateTexts.firstLine,
          firstLineText: dateTexts.firstLine,
          secondLineText: dateTexts.secondLine,
          textAlign: 'right',
          isSortable: true,
          hasLeftBorder: year != previousColumnYear
        }))
        previousColumnYear = year
      }
    }

    return dateRangeColumns
  }

  _createSummaryColumns = () => {
    if (!this.response.data_map || !Object.keys(this.response.data_map).length) return []
    let columns = []
    const dataMapKeys = Object.keys(this.response.data_map)
    const totalSummary = this.response.data_map[dataMapKeys[0]].total_summary
    for (let key in totalSummary) {
      const metricName = this._getMetricName(key)
      const isMainMetric = this._isMainMetric(key)
      const type = this._isString(metricName) ? GridDataTableColumnType.SINGLE_LINE_TEXT : GridDataTableColumnType.DOUBLE_LINE_TEXT

      const column = new GridDataTableColumn({
        key: key,
        type: type,
        text: type === GridDataTableColumnType.SINGLE_LINE_TEXT ? metricName : null,
        firstLineText: type === GridDataTableColumnType.DOUBLE_LINE_TEXT ? metricName.first_line : null,
        secondLineText: type === GridDataTableColumnType.DOUBLE_LINE_TEXT ? metricName.second_line : null,
        textAlign: 'right',
        isSortable: true,
        isArrangable: isMainMetric ? false : true,
        isVisible: isMainMetric
      })

      if (isMainMetric)
        columns.splice(0, 0, column)
      else
        columns.push(column)
    }
    return columns
  }

  _createRows = () => {
    const dataMap = this.response.data_map
    const rowKeys = this.response.target_id_list

    let rows = []
    let self = this

    rowKeys.forEach((rowKey) => {
      const rowValue = dataMap[rowKey]
      const dateMap = rowValue.date_map
      const totalSummary = rowValue.total_summary
      let target = rowValue.target

      if (!target || !target.name) {
        const reportTypeValue = self.reportIncome.reportType.value
        // Undefined data
        if (rowKey === 0) {
          target = {name: self._getUndefinedReportTypeName(reportTypeValue)}
        } else {
          // FETCH DATA FROM STORES
          target = self._findTargetFromStore(reportTypeValue, rowKey)
        }
      }

      rows.push(new GridDataTableRow({
        key: rowKey,
        itemMap: self._createItems(target, dateMap, totalSummary, rowKey)
      }))
    })

    return rows
  }

  _createFooterRow = ($t) => (rows) => {
    if (!rows || !rows.length) return
    const columnKeys = Object.keys(rows[0].itemMap)
    const columnTotalsMap = {}
    for (let i = 0; i < columnKeys.length; i++) {
      columnTotalsMap[columnKeys[i]] = 0
    }

    // Calculate totals
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      for (let columnKey in columnTotalsMap) {
        columnTotalsMap[columnKey] += row.itemMap[columnKey].value
      }
    }

    let footerRowItemMap = {
      target: new ReportIncomeGridDataTableItem({
        columnKey: "target",
        rowKey: "_footer",
        value: $t('generic.field.total'),
        type: ReportIncomeGridDataTableItemType.TEXT,
        isFooterItem: true
      })
    }

    const sampleRowItemMap = rows[0].itemMap
    for (let columnKey in columnTotalsMap) {
      if (columnKey === "target") continue
      const itemType = sampleRowItemMap[columnKey].type
      let itemValue = columnTotalsMap[columnKey]
      let displayValue = roundToThree(itemValue)

      if (itemType === ReportIncomeGridDataTableItemType.CURRENCY) {
        displayValue = formatMoney(itemValue)
      } else if (itemType === ReportIncomeGridDataTableItemType.PERCENT) {
        itemValue = (itemValue / rows.length).toFixed(2)
        displayValue = itemValue + " %"
      }

      footerRowItemMap[columnKey] = new ReportIncomeGridDataTableItem({
        columnKey: columnKey,
        rowKey: "_footer",
        value: itemValue,
        displayValue: displayValue,
        type: sampleRowItemMap[columnKey].type,
        isFooterItem: true
      })
    }

    return new GridDataTableRow({
      key: "_footer",
      itemMap: footerRowItemMap,
      isFooter: true
    })
  }

  getTargetName = (target) => {
    if (target.name) {
      if (target.$parent) {
        if (target.$parent.$parent) {
          return target.$parent.$parent.name + ' / ' +target.$parent.name + ' / ' + target.name
        }
        else {
          return target.$parent.name + ' / ' + target.name
        }
      }
      else {
        return target.name
      }
    }
    else {
      return ''
    }
  }

  _createItems = (target, dateMap, totalSummary, rowKey) => {
    let itemMap = Object.assign({target: this.getTargetName(target)}, dateMap, totalSummary)
    for (let columnKey in itemMap) {
      const itemType = this._getItemType(target, columnKey)

      let item = new ReportIncomeGridDataTableItem({
        columnKey: columnKey,
        rowKey: rowKey,
        value: itemMap[columnKey],
        type: itemType
      })

      if (itemType === ReportIncomeGridDataTableItemType.CURRENCY) {
        item.displayValue = formatMoney(item.value)
      } else if (itemType === ReportIncomeGridDataTableItemType.PERCENT) {
        item.displayValue = item.value + " %"
      }

      // Set item search index
      if (columnKey === "target") {
        item.searchIndex = item.value
      }

      itemMap[columnKey] = item
    }
    return itemMap
  }

  _getItemType = (target, columnKey) => {
    const reportMetric = this.reportIncome.reportMetric
    switch (columnKey) {
      case "target":
        return ReportIncomeGridDataTableItemType.TEXT
      default:
        return ReportIncomeGridDataTableItemType.CURRENCY
    }
  }

  _findTargetFromStore = (reportType, targetId) => {
    let list = null

    switch (reportType) {
      case ReportTypeIncome.ENUM.PAYMENT_TYPE:
        list = this.reportIncome.paymentTypeList
        break;
      case ReportTypeIncome.ENUM.DOCUMENT_TYPE:
        list = this.reportIncome.documentTypeList
        break;
      case ReportTypeIncome.ENUM.CATEGORY:
        list = this.reportIncome.categoryList
        break;
      case ReportTypeIncome.ENUM.VENDOR:
        list = this.reportIncome.vendorList
        break;
      default:
        break;
    }
    if (reportType !== ReportTypeIncome.ENUM.CATEGORY) {
      return list.find((item) => item.id === targetId)
    }
    else {
      let categoryList = list.filter(item => item)
      let allCategoryList = categoryList
      categoryList.filter(item => {
        item.sub_categories.filter(i => {
          allCategoryList.push(i)

          i.sub_categories.filter(s => {
            allCategoryList.push(s)
          })
        })
      })
      return allCategoryList.find((item) => item.id === targetId)
    }
  }

  _getUndefinedReportTypeName = (reportType) => {
    if (reportType === ReportTypeIncome.ENUM.VENDOR) {
      return this.$t('income_report.field.extras.undefined_vendor')
    } else {
      return ""
    }
  }

  _getDateColumnTexts = (dateStr) => {
    const serverDateFormat = "YYYY-MM-DD HH:mm:ss"
    switch (this.reportIncome.group_by) {
      case ReportDateGroupBy.WEEK:
        return {
          firstLine: moment(dateStr, serverDateFormat).week() + ". " + this.$t('generic.dates.week'),
          secondLine: moment(dateStr, serverDateFormat).year()
        }
      case ReportDateGroupBy.MONTH:
        return {
          firstLine: moment(dateStr, serverDateFormat).format("MMM"),
          secondLine: moment(dateStr, serverDateFormat).year()
        }
      case ReportDateGroupBy.QUARTER:
        return {
          firstLine: moment(dateStr, serverDateFormat).quarter() + ". " + this.$t('generic.dates.quarter'),
          secondLine: moment(dateStr, serverDateFormat).year()
        }
      case ReportDateGroupBy.YEAR:
        return {
          firstLine: moment(dateStr, serverDateFormat).year()
        }
      default:
        return {
          firstLine: moment(dateStr, serverDateFormat).format("D MMM"),
          secondLine: moment(dateStr, serverDateFormat).year()
        }
    }
  }

  _getMetricName = (metricKey) => {
    switch (metricKey) {
      case "price":
        return this.$t('income_report.field.metric.incomes_without_tax')
      case "price_with_tax":
        return this.$t('income_report.field.metric.incomes_with_tax')
      case "buy_price":
        return this.$t('income_report.field.metric.buy_price')
      case "gross_profit":
        return this.$t('income_report.field.metric.gross_profit')
      default:
        return null
    }
  }

  _getMetricValue = (metricKey) => {
    switch (metricKey) {
      case "price":
        return ReportMetricIncome.ENUM.PRICES
      case "price_with_tax":
        return ReportMetricIncome.ENUM.PRICES_WITH_TAX
      case "buy_price":
        return ReportMetricIncome.ENUM.BUY_PRICE
      case "gross_profit":
        return ReportMetricIncome.ENUM.GROSS_PROFIT
      default:
        return null
    }
  }

  _isMainMetric = (metricKey) => {
    return this.reportIncome.reportMetric.value === this._getMetricValue(metricKey)
  }

  _isString(myVar) {
    return typeof myVar === 'string' || myVar instanceof String
  }

}

export class ReportIncome {
  constructor(data = {}) {
    this.$t = data.$t
    this.is_unifree_active = data.is_unifree_active


    // Selected filters
    this.incomeType = data.incomeType || this.availableIncomeTypes[1] // IncomeType
    this.reportType = data.reportType || this.availableReportTypes[0] // ReportTypeIncome
    this.reportMetric = data.reportMetric || this.availableReportMetrics[0] // ReportMetricIncome
    this.groupBy = data.groupBy || this.defaultGroupBy // ReportDateGroupBy
    this.dateRange = data.dateRange || this.defaultDateRange // String of date range returned from DatePicker

    // this.branchFilter = data.branchFilter || new Filter({type: FilterTypes.BRANCH})
    this.vendorFilter = data.vendorFilter || new Filter({type: FilterTypes.VENDOR})
    this.tagFilter = data.tagFilter || new Filter({type: FilterTypes.TAG})
    this.categoryFilter = data.categoryFilter || new Filter({type: FilterTypes.CATEGORY})
    this.documentTypeFilter = new Filter({
      type: FilterTypes.CUSTOM,
      customLabel: 'BELGE TÜRÜ',
      customDataList: data.documentTypeList,
      customIdentifier: 'document_type'
    })
    this.paymentTypeFilter = new Filter({
      type: FilterTypes.CUSTOM,
      customLabel: 'ÖDEME TÜRÜ',
      customDataList: data.paymentTypeList,
      customIdentifier: 'payment_type'
    })

    this.filters = [
      this.vendorFilter,
      this.categoryFilter,
      this.documentTypeFilter,
      this.paymentTypeFilter,
      this.tagFilter
    ]
    // Data model for UI
    this.gridDataTableModel = data.gridDataTableModel || null

    // Data lists from stores
    this.vendorList = data.vendorList
    this.documentTypeList = data.documentTypeList
    this.paymentTypeList = data.paymentTypeList
    this.categoryList = data.categoryList
    this.tagList = data.tagList
  }

  // UI
  get availableIncomeTypes() {
    const $t = this.$t
    return [
      new ReportTypeIncome({name: $t('income_report.field.income_type.not_pay'), value: 1}),
      new ReportTypeIncome({name: $t('income_report.field.income_type.pay'), value: 2})
    ]
  }

  get availableReportTypes() {
    const $t = this.$t
    let reportTypes = [
      new ReportTypeIncome({
        name: $t('income_report.field.type.vendor'),
        value: ReportTypeIncome.ENUM.VENDOR
      }),
      new ReportTypeIncome({
        name: $t('income_report.field.type.payment_type'),
        value: ReportTypeIncome.ENUM.PAYMENT_TYPE
      }),
      new ReportTypeIncome({
        name: $t('income_report.field.type.document_type'),
        value: ReportTypeIncome.ENUM.DOCUMENT_TYPE
      }),
      new ReportTypeIncome({
        name: $t('income_report.field.type.category'),
        value: ReportTypeIncome.ENUM.CATEGORY
      })
      //  new ReportTypeIncome({ name: $t('report.income.type.payment_type'), value: 7 })
    ]
    return reportTypes
  }

  get availableReportMetrics() {
    const $t = this.$t
    return [
      new ReportMetricIncome({
        name: $t('income_report.field.metric.incomes_with_tax').full,
        value: ReportMetricIncome.ENUM.PRICES_WITH_TAX
      }),
      new ReportMetricIncome({
        name: $t('income_report.field.metric.incomes_without_tax').full,
        value: ReportMetricIncome.ENUM.PRICES
      }),
      new ReportMetricIncome({
        name: $t('income_report.field.metric.buy_price').full,
        value: ReportMetricIncome.ENUM.BUY_PRICE
      }),
      new ReportMetricIncome({
        name: $t('income_report.field.metric.gross_profit').full,
        value: ReportMetricIncome.ENUM.GROSS_PROFIT
      }),
    ]
  }

  set reportIncomeResponse(response) {
    const gridDataTableFactory = new ReportIncomeGridDataTableFactory(this, response)
    this.gridDataTableModel = gridDataTableFactory.create()
  }

  // Request

  get requestBody() {
    return new ReportIncomeRequest(this)
  }

  get exportRequestBody() {
    return new ReportIncomeExportRequest(this)
  }

  get report_type() {
    return this.reportType ? this.reportType.value : null
  }

  get income_type() {
    return this.incomeType ? this.incomeType.value : null
  }

  get main_metric() {
    return this.reportMetric ? this.reportMetric.value : null
  }

  get group_by() {
    return this.groupBy
  }

  get date_start() {
    return this.dateRange ? this.dateRange.split(" - ")[0] : null
  }

  get date_end() {
    return this.dateRange ? this.dateRange.split(" - ")[1] : null
  }

  get vendor() {
    return this.vendorFilter.value.length ? this.vendorFilter.value.join(",") : null
  }

  get tag() {
    return this.tagFilter.value.length ? this.tagFilter.value.join(",") : null
  }

  get documentType() {
    return this.documentTypeFilter.value.length ? this.documentTypeFilter.value.join(",") : null
  }

  get paymentType() {
    return this.paymentTypeFilter.value.length ? this.paymentTypeFilter.value.join(",") : null
  }

  get category() {
    return this.categoryFilter.value.length ? this.categoryFilter.value.join(",") : null
  }

  get defaultDateRange() {
    let start = moment().subtract(1, 'month')
    let end = moment()
    return start.format("YYYY-MM-DD") + " - " + end.format("YYYY-MM-DD")
  }

  get defaultGroupBy() {
    return ReportDateGroupBy.MONTH
  }

}
